import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FaArrowLeft, FaCheckCircle, FaClock, FaEye, FaHourglassHalf, FaTools, FaUser } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import QuestionsList from '../../Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;

// CircularProgress component (same as AuditOverview)
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754'; // success/green
    if (percent >= 75) return '#0d6efd';  // primary/blue
    if (percent >= 50) return '#ffc107';  // warning/yellow
    if (percent >= 25) return '#fd7e14';  // orange
    return '#dc3545';                      // danger/red
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

function IndividualReAuditOverview() {
  const [allReAudits, setAllReAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    total: 0
  });
  const [activeTab, setActiveTab] = useState('pending');
  
  // New state for re-audit details view
  const [selectedReAudit, setSelectedReAudit] = useState(null);
  const [sectionNames, setSectionNames] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sectionProgress, setSectionProgress] = useState(null);
  const [sectionsProgress, setSectionsProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const [currentLevel, setCurrentLevel] = useState('section');
  const [showNext, setShowNext] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [sectionData, setSectionData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Set initial tab based on URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    
    if (statusParam && ['pending', 'inProgress', 'completed'].includes(statusParam)) {
      setActiveTab(statusParam);
    } else {
      setActiveTab('pending');
    }
  }, [location.search]);

  // Update URL when tab changes
  const handleTabSelect = (selectedTab) => {
    setActiveTab(selectedTab);
    
    const searchParams = new URLSearchParams(location.search);
    if (selectedTab === 'pending') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', selectedTab);
    }
    
    const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    navigate(newUrl, { replace: true });
  };

  // Get status icon based on the status
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return <FaClock className="me-1" size={14} />;
      case 'in progress': return <FaHourglassHalf className="me-1" size={14} />;
      case 'completed': return <FaCheckCircle className="me-1" size={14} />;
      default: return <FaClock className="me-1" size={14} />;
    }
  };

  // Get status color based on the status
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'in progress': return 'primary';
      case 'completed': return 'success';
      default: return 'warning';
    }
  };

  // Fetch all re-audit checklists
  useEffect(() => {
    const fetchAllReAudits = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        
        if (!userData || !userData.BranchCode) {
          throw new Error('Admin data not found');
        }

        // Use the endpoint to get all re-audit checklists for the branch
        const response = await fetch(`${BASE_URL}/get-all-reaudit-checklists/${userData.BranchCode}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch re-audit checklists');
        }

        const data = await response.json();
        const allReAuditChecklists = data.reaudits || [];

        // Process re-audits and add progress data
        let statusStats = { pending: 0, inProgress: 0, completed: 0, total: 0 };
        
        const reauditsWithProgress = await Promise.all(
          allReAuditChecklists.map(async (reaudit) => {
            try {
              // Normalize status
              let normalizedStatus = 'Pending';
              
              if (reaudit.status) {
                const status = reaudit.status.toLowerCase().trim();
                if (status === 'pending' || status === 'not started') {
                  normalizedStatus = 'Pending';
                } else if (status === 'in progress' || status === 'inprogress' || status === 'started') {
                  normalizedStatus = 'In Progress';
                } else if (status === 'completed' || status === 'finished' || status === 'done') {
                  normalizedStatus = 'Completed';
                }
              }

              // Fetch progress data for re-audit
              const progressResponse = await fetch(
                `${BASE_URL}/get-reaudit-progress-status/${reaudit.id}/${userData.BranchCode}/${reaudit.branchId}`
              );
              
              let progressData = {
                progressPercentage: 0,
                totalQuestions: 0,
                answeredQuestions: 0,
                remainingQuestions: 0
              };

              if (progressResponse.ok) {
                const progress = await progressResponse.json();
                progressData = {
                  progressPercentage: progress.progressPercentage || 0,
                  totalQuestions: progress.totalQuestions || 0,
                  answeredQuestions: progress.answeredQuestions || 0,
                  remainingQuestions: progress.remainingQuestions || 0
                };

                // Auto-correct status based on progress
                if (progressData.progressPercentage === 100 && normalizedStatus !== 'Completed') {
                  normalizedStatus = 'Completed';
                } else if (progressData.progressPercentage > 0 && progressData.progressPercentage < 100 && normalizedStatus === 'Pending') {
                  normalizedStatus = 'In Progress';
                }
              }

              // Count for statistics
              if (normalizedStatus === 'Pending') statusStats.pending++;
              else if (normalizedStatus === 'In Progress') statusStats.inProgress++;
              else if (normalizedStatus === 'Completed') statusStats.completed++;

              return {
                ...reaudit,
                status: normalizedStatus,
                ...progressData
              };
              
            } catch (err) {
              console.error(`Error processing re-audit ${reaudit.id}:`, err);
              
              statusStats.pending++;
              
              return {
                ...reaudit,
                status: 'Pending',
                progressPercentage: 0,
                totalQuestions: 0,
                answeredQuestions: 0,
                remainingQuestions: 0
              };
            }
          })
        );

        statusStats.total = reauditsWithProgress.length;
        setStats(statusStats);
        setAllReAudits(reauditsWithProgress);
        
      } catch (err) {
        console.error('Error fetching re-audits:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllReAudits();
  }, []);

  // Functions for re-audit details view (similar to IndividualAuditOverview)
  const handleReAuditClick = async (reaudit) => {
    try {
      setSelectedReAudit(reaudit);
      setIsSectionLoading(true);
      
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData || !userData.BranchCode) {
        throw new Error('User data not found');
      }
      
      // Get re-audit details including sections
      const response = await fetch(`${BASE_URL}/get-reaudit-details/${reaudit.id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch re-audit sections: ${response.status}`);
      }

      const data = await response.json();
      if (!data.sections) {
        throw new Error('No sections data in response');
      }

      const parsedSections = JSON.parse(data.sections);
      setSectionNames(parsedSections.map(s => s.name));
      await loadSavedAnswers(reaudit);
      await fetchAllSectionsProgress(reaudit, parsedSections.map(s => s.name));
      
    } catch (err) {
      console.error('Error fetching re-audit details:', err);
      setError(err.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  // Load saved answers for re-audit
  const loadSavedAnswers = async (reaudit, sectionName = null) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-responses/${reaudit.id}/${reaudit.branch_id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch saved answers');
      }
      
      const data = await response.json();
      
      const relevantAnswers = sectionName 
        ? data.filter(answer => answer.section === sectionName)
        : data;

      const answersMap = {};
      relevantAnswers.forEach(answer => {
        answersMap[answer.questionId] = answer;
      });

      setAnswers(prev => ({
        ...prev,
        ...answersMap
      }));
      
    } catch (error) {
      console.error('Error loading saved answers:', error);
    }
  };

  // Fetch all sections progress for re-audit
  const fetchAllSectionsProgress = async (reaudit, sections) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const progressMap = {};

      for (const section of sections) {
        try {
          const progressResponse = await fetch(
            `${BASE_URL}/get-reaudit-section-progress/${reaudit.id}/${section}`
          );
          
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            progressMap[section] = progressData;
          } else {
            progressMap[section] = {
              progressPercentage: 0,
              answeredQuestions: 0,
              totalQuestions: 0
            };
          }
        } catch (sectionError) {
          console.error(`Error fetching progress for section ${section}:`, sectionError);
          progressMap[section] = {
            progressPercentage: 0,
            answeredQuestions: 0,
            totalQuestions: 0
          };
        }
      }

      setSectionsProgress(progressMap);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleSectionClick = async (sectionName, index) => {
    setSelectedSection(sectionName);
    setIsSectionLoading(true);
    setCurrentPath([sectionName]);
    setShowNext(false);
    setCurrentLevel('section');
  
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-section-details/${selectedReAudit.id}/${encodeURIComponent(sectionName)}`
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch section details');
      }
  
      const data = await response.json();
      setSectionData(data);
      
      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        setSubsections(data.type === 'both' ? (data.subsections || []) : []); // Ensure it's always an array
        setShowNext(data.type === 'both' && (data.subsections || []).length > 0);
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.data || []); // Ensure it's always an array
      }
  
      await loadSavedAnswers(selectedReAudit, sectionName);
      await fetchSectionProgress([sectionName]);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const handleItemClick = async (itemName, itemType, parentPath = []) => {
    setIsSectionLoading(true);
    const newPath = [...parentPath, itemName];
    setCurrentPath(newPath);
    setShowNext(false);
    
    try {
      const pathParam = newPath.join('|');
      
      // Fetch section progress first
      await fetchSectionProgress(newPath);

      const response = await fetch(
        `${BASE_URL}/get-reaudit-nested-details/${selectedReAudit.id}/${encodeURIComponent(pathParam)}`
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }
  
      const data = await response.json();
      setSectionData(data);
      
      const nextLevel = newPath.length === 1 ? 'subsection' : 
                       newPath.length === 2 ? 'subsubsection' : 
                       'final';
      setCurrentLevel(nextLevel);

      if (data.type === 'questions') {
        setQuestions(data.questions || []);
        setSubsections([]);
        setShowNext(false);
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.subsections || []);
        
        // Fetch progress for each subsection
        const subsectionPromises = data.subsections.map(async (subsection) => {
          const subsectionPath = [...newPath, subsection.name].join('|');
          try {
            const progressResponse = await fetch(
              `${BASE_URL}/get-reaudit-section-progress/${selectedReAudit.id}/${encodeURIComponent(subsectionPath)}`
            );
            if (progressResponse.ok) {
              const progressData = await progressResponse.json();
              setSubsectionProgress(prev => ({
                ...prev,
                [subsectionPath]: progressData
              }));
            }
          } catch (err) {
            console.error(`Error fetching progress for ${subsectionPath}:`, err);
          }
        });
        
        await Promise.all(subsectionPromises);
        setShowNext(false);
      } else if (data.type === 'both') {
        setQuestions(data.questions || []);
        setSubsections(data.subsections || []);
        
        // Fetch progress for each subsection
        const subsectionPromises = data.subsections.map(async (subsection) => {
          const subsectionPath = [...newPath, subsection.name].join('|');
          try {
            const progressResponse = await fetch(
              `${BASE_URL}/get-reaudit-section-progress/${selectedReAudit.id}/${encodeURIComponent(subsectionPath)}`
            );
            if (progressResponse.ok) {
              const progressData = await progressResponse.json();
              setSubsectionProgress(prev => ({
                ...prev,
                [subsectionPath]: progressData
              }));
            }
          } catch (err) {
            console.error(`Error fetching progress for ${subsectionPath}:`, err);
          }
        });
        
        await Promise.all(subsectionPromises);
        setShowNext((data.subsections || []).length > 0);
      }
      
      await loadSavedAnswers(selectedReAudit, newPath[0]);

    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const fetchSectionProgress = async (path = []) => {
    try {
      const pathParam = path.length > 0 ? path.join('|') : '';
      const response = await fetch(
        `${BASE_URL}/get-reaudit-section-progress/${selectedReAudit.id}/${encodeURIComponent(pathParam)}`
      );

      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      setSectionProgress(data);

      // Also update the subsection progress map
      setSubsectionProgress(prev => ({
        ...prev,
        [pathParam]: data
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Navigation functions
  const navigateToPath = (index) => {
    const newPath = currentPath.slice(0, index + 1);
    handleItemClick(newPath[newPath.length - 1], 'section', newPath.slice(0, -1));
  };

  const resetNavigation = () => {
    setSelectedSection(null);
    setSubsections([]);
    setQuestions([]);
    setCurrentPath([]);
  };

  const backToReAuditList = () => {
    setSelectedReAudit(null);
    setSectionNames([]);
    resetNavigation();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getProgressColor = (percentage, checklistStatus) => {
    if (checklistStatus === 'Completed') return 'success';
    if (percentage === 100) return 'success';
    if (percentage >= 60) return 'primary';
    if (percentage >= 30) return 'info';
    return 'warning';
  };

  const getFilteredReAudits = () => {
    if (activeTab === 'pending') return allReAudits.filter(reaudit => reaudit.status?.toLowerCase() === 'pending');
    if (activeTab === 'inProgress') return allReAudits.filter(reaudit => reaudit.status?.toLowerCase() === 'in progress');
    if (activeTab === 'completed') return allReAudits.filter(reaudit => reaudit.status?.toLowerCase() === 'completed');
    return allReAudits.filter(reaudit => reaudit.status?.toLowerCase() === 'pending');
  };

  const filteredReAudits = getFilteredReAudits();


  // If a re-audit is selected, show the re-audit details view
  if (selectedReAudit) {
    return (
      <Container fluid className="py-4 view-only">
        {/* Header */}
        <Row className="mb-4">
          <Col>
                <div className="d-flex align-items-center mb-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-3"
                    onClick={backToReAuditList}
                  >
                    <FaArrowLeft className="me-1" />
                    Back to List
                  </Button>
            
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    resetNavigation();
                  }}>Sections</a>
                </li>
                
                {currentPath.map((pathItem, index) => (
                  <li 
                    key={index} 
                    className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                  >
                    {index === currentPath.length - 1 ? (
                      <span>{pathItem}</span>
                    ) : (
                      <a href="#" onClick={(e) => {
                        e.preventDefault();
                        navigateToPath(index);
                      }}>{pathItem}</a>
                    )}
                  </li>
                ))}
                
                {questions.length > 0 && currentPath.length > 0 && (
                  <li className="breadcrumb-item active">Questions</li>
                )}
              </ol>
            </nav>
          </Col>
        </Row>


        {isSectionLoading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Section Data</p>
            <p className="text-muted">Please wait...</p>
          </div>
        ) : (
          <>
  {/* Show sections if no section is selected */}
  {currentPath.length === 0 ? (
    <Row xs={1} md={2} lg={3} className="g-4">
      {sectionNames.map((sectionName, index) => {
        const progress = sectionsProgress[sectionName] || {
          progressPercentage: 0,
          answeredQuestions: 0,
          totalQuestions: 0
        };

        return (
          <Col key={index}>
            <Card 
              className="h-100 shadow-sm hover-shadow"
              onClick={() => handleSectionClick(sectionName, index)}
              role="button"
            >
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <CircularProgress 
                    percentage={progress.progressPercentage}
                    size={80}
                    showLabel={true}
                  />
                </div>
                <div className="text-center mb-3">
                  <h5 className="mb-1">{sectionName}</h5>
                  <small className="text-muted d-block">
                    {progress.totalQuestions} Questions
                  </small>
                </div>
                <div className="mt-auto">
                  <div className="d-flex justify-content-center gap-3 mb-2">
                    <div className="text-success">
                      <small>{progress.answeredQuestions} Answered</small>
                    </div>
                    <div className="text-danger">
                      <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                    </div>
                  </div>
                  <div className="text-center">
                    <small className={`badge bg-${
                      progress.progressPercentage === 100 ? 'success' :
                      progress.progressPercentage > 0 ? 'warning' : 'danger'
                    }`}>
                      {progress.progressPercentage === 100 ? 'Completed' :
                      progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  ) : ( 
    <>
      {/* Show questions if they exist */}
      {questions.length > 0 && (
        <QuestionsList
          questions={questions}
          answers={answers}
          handleAnswerChange={() => {}} // Empty function for view-only
          setAnswers={() => {}} // Empty function for view-only
          selectedSection={currentPath[currentPath.length - 1]}
          checklist={selectedReAudit}
          auditorId={selectedReAudit.auditorId}
          isViewOnly={true} // Always view-only for admin
        />
      )}

      {/* Show subsections if they exist - FIXED CONDITION AND LOGIC */}
      {subsections.length > 0 && (
        <>
          {/* Add separator if both questions and subsections exist */}
          {questions.length > 0 && (
            <div className="my-4">
              <hr />
              <h5 className="text-center text-muted">Sub-sections</h5>
            </div>
          )}
          
          <Row xs={1} md={2} lg={3} className="g-4">
            {subsections.map((subsection, index) => {
              const subsectionPath = [...currentPath, subsection.name].join('|');
              const progress = subsectionProgress[subsectionPath] || {
                progressPercentage: 0,
                answeredQuestions: 0,
                totalQuestions: 0
              };

              return (
                <Col key={index}>
                  <Card 
                    className="h-100 shadow-sm hover-shadow"
                    onClick={() => handleItemClick(
                      subsection.name, 
                      currentLevel, 
                      currentPath
                    )}
                    role="button"
                  >
                    <Card.Body className="d-flex flex-column">
                      <div className="text-center mb-3">
                        <CircularProgress 
                          percentage={progress.progressPercentage} 
                          size={80}
                          showLabel={true}
                        />
                      </div>
                      <div className="text-center mb-3">
                        <h5 className="mb-1">{subsection.name}</h5>
                        <small className="text-muted d-block">
                          {progress.totalQuestions} Questions
                        </small>
                      </div>
                      <div className="mt-auto">
                        <div className="d-flex justify-content-center gap-3 mb-2">
                          <div className="text-success">
                            <small>{progress.answeredQuestions} Answered</small>
                          </div>
                          <div className="text-danger">
                            <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                          </div>
                        </div>
                        <div className="text-center">
                          <small className={`badge bg-${
                            progress.progressPercentage === 100 ? 'success' :
                            progress.progressPercentage > 0 ? 'warning' : 'danger'
                          }`}>
                            {progress.progressPercentage === 100 ? 'Completed' :
                            progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                          </small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      {/* No content found - ONLY show if both questions AND subsections are empty */}
      {questions.length === 0 && subsections.length === 0 && (
        <div className="text-center py-5">
          <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
          <h4 className="text-muted">No content found</h4>
          <p className="text-muted">This section appears to be empty or still loading.</p>
          <Button 
            variant="outline-primary" 
            className="mt-3"
            onClick={resetNavigation}
          >
            Return to Sections
          </Button>
        </div>
      )}

      {/* Navigation buttons - show if ANY content exists */}
      {(questions.length > 0 || subsections.length > 0) && (
        <div className="navigation-buttons d-flex justify-content-between mt-4"
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          right: '1rem',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}
        >
          <Button 
            variant="outline-secondary"
            onClick={() => {
              if (currentPath.length === 1) {
                resetNavigation();
              } else {
                navigateToPath(currentPath.length - 2);
              }
            }}
          >
            <FaArrowLeft className="me-2" />
            Back
          </Button>
        </div>
      )}
    </>
  )}
</>
)}

        

        {/* Same styles as IndividualAuditOverview */}
        <style jsx>{`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
          }
          .circular-progress {
            position: relative;
            margin-left: 1rem;
          }
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          .progress-text-large {
            font-size: 24px;
            font-weight: 700;
            transition: fill 0.35s;
          }
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }
          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }

          /* View-only mode styles */
          .view-only input,
          .view-only textarea,
          .view-only select,
          .view-only .form-control {
            pointer-events: none;
            background-color: #f8f9fa !important;
            border-color: #dee2e6 !important;
            cursor: not-allowed;
          }

          .view-only .btn-primary,
          .view-only .btn-success,
          .view-only .btn-info {
            display: none;
          }

          .view-only .file-upload-area,
          .view-only .file-upload-button {
            pointer-events: none;
            opacity: 0.6;
            background-color: #f8f9fa !important;
          }

          .view-only .btn-outline-danger {
            display: none;
          }

          .view-only .form-check-input {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }

          .view-only .form-range {
            pointer-events: none;
            opacity: 0.6;
          }

          .view-only .navigation-buttons .btn-outline-secondary {
            pointer-events: auto;
            opacity: 1;
            background-color: transparent !important;
          }

          .alert-info {
            border-left: 4px solid #0dcaf0;
            background-color: #cff4fc;
            border-color: #b8e6f0;
            color: #055160;
          }
        `}</style>
      </Container>
    );
  }



  // Original re-audit list view
  return (
    <Container fluid className="p-0">
      {/* Header */}
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-1">
                Corrective Action Plan Overview
              </h4>
              <p className="mb-0 opacity-75">Monitor all corrective action plan across different statuses</p>
            </Col>
          </Row>
        </Container>

      {/* Stats Cards */}
      <div className="px-4 py-4">
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Re-Audits</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-warning mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-warning fw-bold mb-1">Loading All Re-Audits</p>
            <p className="text-muted">Please wait...</p>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 pt-4 pb-0 px-4">
              {/* Custom Tab Navigation */}
              <div className="custom-tabs mb-3">
                <button
                  className={`custom-tab-button ${activeTab === 'pending' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('pending')}
                >
                  <FaClock className="me-1" size={14} />
                  Plan ({stats.pending})
                </button>
                <button
                  className={`custom-tab-button ${activeTab === 'inProgress' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('inProgress')}
                >
                  <FaHourglassHalf className="me-1" size={14} />
                  In Progress ({stats.inProgress})
                </button>
                <button
                  className={`custom-tab-button ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => handleTabSelect('completed')}
                >
                  <FaCheckCircle className="me-1" size={14} />
                  Completed ({stats.completed})
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {filteredReAudits.length === 0 ? (
                <div className="text-center py-5">
                  <div className="empty-state-icon bg-light text-muted mb-3">
                    <FaTools size={32} />
                  </div>
                  <h5>No {activeTab !== 'pending' ? activeTab : ''} Re-Audit Checklists Found</h5>
                  <p className="text-muted mb-4">
                    {activeTab === 'pending' 
                      ? 'No pending re-audit checklists are available at the moment.'
                      : `No ${activeTab} re-audit checklists are available at the moment.`}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Re-Audit</th>
                        <th>Original Audit</th>
                        <th>Branch</th>
                        <th>Auditor</th>
                        <th>Created Date</th>
                        <th>Progress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReAudits.map((reaudit) => (
                        <tr key={`${reaudit.id}-${reaudit.branchId}-${reaudit.status}`}>
                          <td>
                            <div className="fw-medium">{reaudit.checklist_name}</div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {reaudit.original_checklist_name || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {reaudit.branchName || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <FaUser className="text-muted me-2" size={12} />
                              {reaudit.auditorName || 'Not assigned'}
                            </div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {formatDate(reaudit.createdAt || reaudit.created_at)}
                            </div>
                          </td>
                          <td style={{ minWidth: '200px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small>Progress</small>
                              <Badge 
                                bg={getProgressColor(reaudit.progressPercentage || 0, reaudit.status)}
                              >
                                {reaudit.status?.toLowerCase() === 'pending' ? 'Not Started' : `${Math.round(reaudit.progressPercentage || 0)}%`}
                              </Badge>
                            </div>
                            <div className="progress" style={{ height: '8px' }}>
                              <div 
                                className={`progress-bar bg-${getProgressColor(reaudit.progressPercentage || 0, reaudit.status)}`} 
                                role="progressbar" 
                                style={{ width: `${reaudit.progressPercentage || 0}%` }}
                                aria-valuenow={reaudit.progressPercentage || 0} 
                                aria-valuemin="0" 
                                aria-valuemax="100">
                              </div>
                            </div>
                            <div className="small mt-1 text-muted">
                              {reaudit.status?.toLowerCase() === 'pending' ? 
                                (reaudit.totalQuestions ? `Not started - ${reaudit.totalQuestions} questions` : 'Not started yet') :
                                (reaudit.totalQuestions && reaudit.totalQuestions > 0) ? 
                                  `${reaudit.answeredQuestions || 0}/${reaudit.totalQuestions} questions` : 
                                  'No data'
                              }
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleReAuditClick(reaudit)}
                              className="d-flex align-items-center"
                            >
                              <FaEye className="me-1" />
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        
      </div>
      

      <style jsx>{`
      
        .empty-state-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin: 0 auto;
        }

        .table th {
          font-weight: 600;
          color: #344767;
          border-bottom: 2px solid #e9ecef;
        }
        
        .progress {
          border-radius: 10px;
          background-color: #f0f2f5;
        }
        
        .progress-bar {
          border-radius: 10px;
        }

        .card {
          transition: transform 0.2s ease-in-out;
        }

        .card:hover {
          transform: translateY(-2px);
        }
        
        /* Custom Tab Styles */
        .custom-tabs {
          display: flex;
          border-bottom: 1px solid #dee2e6;
        }
        
        .custom-tab-button {
          font-weight: 600;
          color: #495057;
          border: none;
          background: transparent;
          border-bottom: 2px solid transparent;
          padding: 0.5rem 1rem;
          margin-right: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .custom-tab-button:hover {
          color: #f39c12;
          border-color: transparent;
        }
        
        .custom-tab-button.active {
          color: #f39c12;
          border-bottom: 2px solid #f39c12;
          background: transparent;
        }
      `}</style>
    </Container>
  );
}

export default IndividualReAuditOverview;